/**
 * Arabic plurals for six-form messages, and the numbers every message is given (spec §7 P-7.3).
 *
 * vue-i18n's own rule is English-shaped: with more than two forms it picks `min(n, 2)`, so a
 * six-form Arabic message would print its dual form for 3 and for 300. Only a SIX-form
 * message is read the Arabic way; every other length keeps vue-i18n's own default, so the
 * Arabic strings that still carry two or three forms render exactly as before.
 */
import { toIntlLocale } from './intl.js';

const INDEX_BY_CATEGORY = {
  zero: 0,
  one: 1,
  two: 2,
  few: 3,
  many: 4,
  other: 5,
};

const arabicRules = new Intl.PluralRules('ar');

/** vue-i18n 9's built-in choice rule, reproduced so a non-six-form message is unchanged. */
export const defaultChoice = (choice, choicesLength) => {
  const n = Math.abs(choice);
  if (choicesLength === 2) {
    if (!n) return 1;
    return n > 1 ? 1 : 0;
  }
  return n ? Math.min(n, 2) : 0;
};

export const arabicChoice = (choice, choicesLength) => {
  if (choicesLength !== 6) return defaultChoice(choice, choicesLength);
  return INDEX_BY_CATEGORY[arabicRules.select(Math.abs(choice))] ?? 5;
};

// ── THE NUMBERS A MESSAGE IS GIVEN (spec §7 P-7.3) ─────────────────────────────────────
// `useWasloI18n`'s `t` runs every call's arguments through `localisedArgs`, so no component has
// to remember two rules:
//   1. Every NUMBER in the named values is written by the formatter (`toIntlLocale`, so
//      Arabic-Indic for Arabic, O-5). A raw `{ count: 3 }` used to print "3" inside an Arabic
//      sentence, beside "٣" from a component that had formatted its own.
//   2. The plural choice is EXPLICIT. vue-i18n picks a form from a NUMERIC `count` or `n` and
//      from nothing else; a count a component already formatted ("١٢") is a string, and the
//      message then fell back to its "one" form. So the choice is taken from the caller's own
//      plural argument, else from the first of `count`, `n`, `ways`, `days`, `total` that holds a
//      number, formatted or not.

/** A number written by the formatter in the screen's language. */
export const formatCount = (value, locale, options = {}) =>
  new Intl.NumberFormat(toIntlLocale(locale), options).format(value);

/** The marks a formatted number may carry besides its digits: grouping, spaces, bidi marks. */
const NUMBER_NOISE = /[,\u066C\u00A0\u202F\u200E\u200F\u061C\s]/g;

/**
 * A number back out of a count, raw or already formatted ("١٢", "1,024", "٢٠\u066C٠٠٠"), or
 * `undefined` when it is not one ("Room 3", "2:41", "").
 */
export const countOf = value => {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const ascii = value
    .replace(/[٠-٩]/g, digit => String(digit.charCodeAt(0) - 0x0660))
    .replace(/\u066B/g, '.')
    .replace(NUMBER_NOISE, '');
  return /^-?\d+(?:\.\d+)?$/.test(ascii) ? Number(ascii) : undefined;
};

/** The named values a plural choice is read from, in order. */
export const PLURAL_SOURCES = ['count', 'n', 'ways', 'days', 'total'];

const pluralFrom = named =>
  PLURAL_SOURCES.map(key => countOf(named[key])).find(
    value => value !== undefined
  );

const isNamed = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isPhrase = value =>
  isNamed(value) &&
  typeof value.unit === 'string' &&
  typeof value.count === 'number';

/**
 * `t`'s arguments after the key, rewritten by the two rules above. `facts` are the numbers a
 * message states as a fact of the product (`helpers/copy-facts.js`), given as named values the
 * caller's own override; a `{ unit, count }` fact is written by `phrase` (the number with its
 * noun). Any other shape (a default message, an options object) passes through.
 */
export const localisedArgs = (args, locale, facts, phrase) => {
  const [first, second, ...rest] = args;
  const plain =
    first === undefined || typeof first === 'number' || isNamed(first);
  if (!plain || (second !== undefined && typeof second !== 'number'))
    return args;
  if (first === undefined && !facts) return args;
  const raw =
    typeof first === 'number'
      ? { ...facts, count: first, n: first }
      : { ...facts, ...(first ?? {}) };
  const named = Object.fromEntries(
    Object.entries(raw).map(([key, value]) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return [key, formatCount(value, locale)];
      }
      if (isPhrase(value) && phrase)
        return [key, phrase(value.unit, value.count)];
      return [key, value];
    })
  );
  const plural = typeof second === 'number' ? second : pluralFrom(raw);
  return plural === undefined ? [named] : [named, plural, ...rest];
};
